import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getBotUsageAdmin,
  getMyBotUsage,
  getUnattributedClaudeCodeUsage,
  getClaudeSeatCounts,
  getClaudeCodeIngestTokenStatus,
  rotateClaudeCodeIngestToken,
  type BotUsageAdminRow,
  type BotUsageSelfRow,
  type ClaudeCodeIngestTokenRotation,
  type ClaudeCodeIngestTokenStatus,
  type ClaudeSeatCounts,
  type UnattributedClaudeCodeUsage,
} from '../services/api/botUsage';

/**
 * Phase 87 (Plan 87-08) — TanStack Query hooks for the D-07 admin Usage tab
 * and the D-08 Profile self-view gauges. Both entry points are strictly
 * read-only (the backend read service never triggers an Anthropic poll), so
 * there are no mutations or invalidation here.
 */

export function useBotUsageAdmin() {
  return useQuery<BotUsageAdminRow[]>({
    queryKey: ['bot-usage-admin'],
    queryFn: getBotUsageAdmin,
    staleTime: 30 * 1000,
  });
}

export function useMyBotUsage() {
  return useQuery<BotUsageSelfRow | null>({
    queryKey: ['bot-usage-me'],
    queryFn: getMyBotUsage,
    staleTime: 30 * 1000,
  });
}

/**
 * MKS-298 — admin-only. `enabled` lets the Usage tab skip the request
 * entirely while the release flag is off, so a flag-off render makes exactly
 * the same requests it made before the ticket.
 */
/** Granted Claude seats per tier plus the seat holders not connected to Monka. */
export function useClaudeSeatCounts() {
  return useQuery<ClaudeSeatCounts>({
    queryKey: ['claude-seat-counts'],
    queryFn: getClaudeSeatCounts,
    staleTime: 30 * 1000,
  });
}

export function useUnattributedClaudeCodeUsage(enabled = true) {
  return useQuery<UnattributedClaudeCodeUsage[]>({
    queryKey: ['claude-code-usage-unattributed'],
    queryFn: getUnattributedClaudeCodeUsage,
    staleTime: 30 * 1000,
    enabled,
  });
}

export function useClaudeCodeIngestTokenStatus(enabled = true) {
  return useQuery<ClaudeCodeIngestTokenStatus>({
    queryKey: ['claude-code-ingest-token'],
    queryFn: getClaudeCodeIngestTokenStatus,
    staleTime: 30 * 1000,
    enabled,
  });
}

/**
 * Rotating returns the new token ONCE; the component holds it in local state
 * for the admin to copy. The status query is invalidated so the tail and
 * rotation time refresh, and nothing caches the token itself.
 */
export function useRotateClaudeCodeIngestToken() {
  const queryClient = useQueryClient();
  return useMutation<ClaudeCodeIngestTokenRotation>({
    mutationFn: rotateClaudeCodeIngestToken,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claude-code-ingest-token'] });
    },
  });
}
